/**
 * Minimal promise-wrapped IndexedDB store for the SWR cache.
 *
 * Exists because `localStorage` is capped at ~5 MB per origin and stores UTF-16,
 * so the `/api/events/published` payload (~2.3M chars even after the field
 * allowlist) can never be persisted there — it would occupy ~4.6 MB on its own.
 * IndexedDB quotas are orders of magnitude larger and it stores structured
 * clones rather than strings, so there's no JSON.stringify cost on write or
 * JSON.parse cost on read.
 *
 * Deliberately dependency-free and tiny: we need get/set/delete/prefix-delete,
 * not a wrapper library.
 *
 * Every operation is fail-soft. IndexedDB is unavailable or throws in several
 * real situations — Firefox private browsing, Safari with storage restrictions,
 * blocked third-party contexts, a version-change deadlock — and this is a cache.
 * A failure must degrade to "no cache", never to a broken page.
 */

const DB_NAME = 'mhe-swr';
const DB_VERSION = 1;
const STORE = 'entries';

/** Never resolve into a hung `open()`: a blocked upgrade can hang forever. */
const OPEN_TIMEOUT_MS = 3000;

export interface IdbEntry<T> {
  key: string;
  ts: number;
  data: T;
}

let dbPromise: Promise<IDBDatabase | null> | null = null;

function openDb(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise<IDBDatabase | null>((resolve) => {
    try {
      if (typeof indexedDB === 'undefined') return resolve(null);
      const req = indexedDB.open(DB_NAME, DB_VERSION);

      // An open() that never fires any event (upgrade blocked by another tab)
      // would leave every caller awaiting forever, so cap it.
      const timer = setTimeout(() => resolve(null), OPEN_TIMEOUT_MS);
      const done = (v: IDBDatabase | null) => { clearTimeout(timer); resolve(v); };

      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'key' });
      };
      req.onsuccess = () => done(req.result);
      req.onerror = () => done(null);
      req.onblocked = () => done(null);
    } catch {
      resolve(null);
    }
  });
  return dbPromise;
}

function tx<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest): Promise<T | null> {
  return openDb().then((db) => {
    if (!db) return null;
    return new Promise<T | null>((resolve) => {
      try {
        const t = db.transaction(STORE, mode);
        const req = fn(t.objectStore(STORE));
        req.onsuccess = () => resolve(req.result as T);
        req.onerror = () => resolve(null);
        t.onabort = () => resolve(null);
        t.onerror = () => resolve(null);
      } catch {
        resolve(null);
      }
    });
  }).catch(() => null);
}

export function idbGet<T>(key: string): Promise<IdbEntry<T> | null> {
  return tx<IdbEntry<T>>('readonly', (s) => s.get(key));
}

export function idbSet<T>(key: string, ts: number, data: T): Promise<void> {
  return tx('readwrite', (s) => s.put({ key, ts, data })).then(() => undefined);
}

export function idbDelete(key: string): Promise<void> {
  return tx('readwrite', (s) => s.delete(key)).then(() => undefined);
}

/** Delete every entry whose key starts with `prefix`. */
export async function idbDeleteByPrefix(prefix: string): Promise<void> {
  const keys = await tx<IDBValidKey[]>('readonly', (s) => s.getAllKeys());
  if (!keys) return;
  await Promise.all(
    keys
      .filter((k): k is string => typeof k === 'string' && k.startsWith(prefix))
      .map((k) => idbDelete(k))
  );
}

/** Drop entries older than `maxAgeMs`, so abandoned keys don't accumulate. */
export async function idbEvictOlderThan(maxAgeMs: number): Promise<void> {
  const all = await tx<IdbEntry<unknown>[]>('readonly', (s) => s.getAll());
  if (!all) return;
  const cutoff = Date.now() - maxAgeMs;
  await Promise.all(all.filter((e) => !e || typeof e.ts !== 'number' || e.ts < cutoff).map((e) => idbDelete(e.key)));
}
